import { last } from 'lodash';
import { isMobile, isTablet } from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import config from '../config';
import {
  BaseOnboardingEventProperties,
  PartialUserData,
} from '../hooks/useOnboardingTracking';
import { LoanOfficerDetailDto, PartnerDto } from '../openapi/atlantis';
import { OnboardingResponseRoleEnum, UserResponse } from '../openapi/yenta';

const ANALYTICS_SESSION_KEY = 'analytics_session_id';

/**
 * Detects the device category using react-device-detect library
 * More reliable than user-agent parsing
 * @returns 'mobile', 'tablet', or 'desktop'
 */
export const getDeviceCategory = (): string => {
  if (isTablet) return 'tablet';
  if (isMobile) return 'mobile';
  return 'desktop';
};

/**
 * Generates a cryptographically secure unique identifier using uuid library
 * @returns UUID v4 string
 */
const generateSessionId = (): string => {
  return uuidv4();
};

/**
 * Gets or creates a session ID for analytics tracking
 * Session ID persists for the browser session using sessionStorage
 * @returns Session ID string
 */
export const getSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem(ANALYTICS_SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    // Handle sessionStorage errors (e.g., in private browsing mode)
    // Generate temporary session ID without storage
    return generateSessionId();
  }
};

/**
 * Retrieves the Google Analytics client ID (GA4)
 * Client ID is a unique identifier assigned by GA to each user
 * @returns Promise that resolves to client ID string, or 'unknown' if GA is not available
 */
export const getGAClientId = async (): Promise<string> => {
  return new Promise((resolve) => {
    try {
      // Check if gtag is available
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag(
          'get',
          config.googleAnalyticsMeasurementId,
          'client_id',
          (clientId: string) => {
            resolve(clientId || 'unknown');
          },
        );
      } else {
        // GA not loaded (e.g., test environment, ad blocker)
        resolve('unknown');
      }
    } catch (error) {
      // Handle any errors gracefully
      resolve('unknown');
    }
  });
};

/**
 * Constructs base onboarding event properties from user data
 * Handles cases where user data may not be available (e.g., during registration)
 * @param user User response object from API (optional, may be null/undefined)
 * @param screenName Current screen/page name
 * @param buttonText Optional button text that triggered the event
 * @param partialUserData Optional form data from registration (before account creation)
 * @returns Base event properties object
 */
export const getBaseOnboardingProperties = (
  user: UserResponse | null | undefined,
  screenName: string,
  buttonText?: string,
  partialUserData?: PartialUserData,
): BaseOnboardingEventProperties => {
  // Get applicant_id from onboarding data (prioritize AGENT role)
  const agentOnboarding = (user?.onboardings || [])?.find(
    (o) =>
      o?.role === OnboardingResponseRoleEnum.Agent &&
      !!o?.sherlockApplicationId,
  );
  const applicantId =
    agentOnboarding?.legacyApplicationId ||
    last(user?.applications || [])?.id ||
    'unknown';

  // Prioritize authenticated user data, fall back to form data, then 'unknown'
  const firstName = user?.firstName || partialUserData?.firstName || '';
  const lastName = user?.lastName || partialUserData?.lastName || '';
  const email = user?.emailAddress || partialUserData?.emailAddress || '';

  return {
    applicant_id: applicantId,
    user_id: user?.id || 'unknown',
    user_first_name: firstName,
    user_last_name: lastName,
    user_email_address: email,
    timestamp: new Date().toISOString(),
    user_platform: 'onereal',
    button_text: buttonText,
    session_id: getSessionId(),
    screen_name: screenName,
    device_category: getDeviceCategory(),
    page_url: window.location.href,
  };
};

/**
 * Constructs event properties for agent partner page tracking
 * Handles cases where user may not be authenticated (typical for landing pages)
 * @param loanOfficer Loan officer details from the page
 * @param partner Partner details from the page
 * @returns Promise that resolves to event properties object with all required fields
 */
export const getAgentPartnerEventProperties = async (
  loanOfficer: LoanOfficerDetailDto | undefined,
  partner: PartnerDto | undefined,
): Promise<Record<string, unknown>> => {
  const clientId = await getGAClientId();

  return {
    customer_id: clientId,
    loan_officer_user_id: loanOfficer?.userId || 'unknown',
    loan_officer_user_email: loanOfficer?.emailAddress || 'unknown',
    partner_user_id: partner?.userId || 'unknown',
    partner_user_email: partner?.emailAddress || 'unknown',
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    page_url: window.location.href,
    page_title: document.title,
    device_category: getDeviceCategory(),
    platform_type: 'onereal',
  };
};
